// components/CashInventoryChart.jsx
import {
    Box,
    Card,
    Typography
} from "@mui/material";

import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    LabelList
} from "recharts";

export default function CashInventoryChart({
    title,
    xLabels,
    series,
    dataMatrix,
    yTicks,
    yAxisLabel = "Dollars ($M)",
    valueFormatter = (v) => `$${v}M`
}) {

    // ---- Convert dataMatrix -> rows for Recharts ----
    const rows = xLabels.map((label, rowIndex) => {
        const row = { month: label };
        series.forEach((s, colIndex) => {
            row[s.key] = dataMatrix[rowIndex]?.[colIndex] ?? 0;
        });
        return row;
    });

    // ---- Y Axis Range ----
    const topValue = yTicks[yTicks.length - 1];

    const chartWidth = Math.max(rows.length * 90, 900);

    return (
        <Card
            sx={{
                width: "100%",
                borderRadius: "12px !important",
                background: "#fff",
                overflow: "visible",
                 boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
                position: "relative"
            }}
        >
            {/* ==== HEADER ==== */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-10px",
                    left: "12px",
                    right: "12px",
                    background: "#115f9c",
                    height: "36px",
                    borderRadius: "4px !important",
                    display: "flex",
                    alignItems: "center",
                    px: "12px",
                    color: "#fff",
                    fontSize: { xs: 12, sm: 12, md: 16 },
                    fontWeight: 200,
                    boxShadow:"0px 1px 16px rgba(128, 203, 255, 0.5)"
                }}
            >
                {title}
            </Box>

            {/* ==== CHART ==== */}
            <Box sx={{
                width: "100%",
                height: 420,
                p: 2,
                overflowX: "auto",
                overflowY: "hidden",
            }}
            >
                <Box
                    sx={{
                        width: {
                            xs: chartWidth,   //  enable horizontal scroll on mobile
                            sm: chartWidth,   // enable horizontal scroll on tablet
                            md: chartWidth,
                            lg: "100%"
                        },
                        minWidth: {
                            xs: chartWidth,
                            sm: chartWidth,
                            md: chartWidth,
                            lg: "100%"
                        },
                        height: "100%",
                    }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={rows}
                            margin={{ top: 50, right: 30, left: 0, bottom: 10 }}
                        >
                            <CartesianGrid stroke="#1111110D" vertical={false} />

                            <XAxis
                                dataKey="month"
                                tick={{ fontSize: 10, color: "#111111", fontWeight: 200, }}
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                padding={{ left: 55, right: 10 }}
                            />

                            <YAxis
                                domain={[0, topValue]}
                                ticks={yTicks}
                                tick={{ fontSize: 10, fill: "#11111180", dx: -12, fontWeight: 200 }} // move labels left
                                axisLine={false}
                                tickLine={false}
                                label={{
                                    value: yAxisLabel,
                                    angle: -90,
                                    position: "insideLeft",
                                    dx: 5,
                                    dy: 40,
                                    style: { fontSize: 10, fill: "#111111", fontWeight: 200 }
                                }}
                            // tickFormatter={v => `$${v}M`}
                            />

                            <Tooltip formatter={valueFormatter} />

                            {/* ==== DYNAMIC SERIES RENDERING ==== */}
                            {series.map(s => {
                                if (s.type === "bar") {
                                    return (
                                        <Bar
                                            key={s.key}
                                            dataKey={s.key}
                                            fill={s.color}
                                            barSize={22}
                                        />
                                    );
                                }

                                if (s.type === "line") {
                                    return (
                                        <Line
                                            key={s.key}
                                            type="linear"
                                            dataKey={s.key}
                                            stroke={s.color}
                                            strokeWidth={3}
                                            dot={{
                                                r: 4,
                                                fill: s.color,
                                                stroke: s.color,
                                                strokeWidth: 2
                                            }}
                                            activeDot={{
                                                r: 6,
                                                fill: "#fff",
                                                stroke: s.color,
                                                strokeWidth: 2
                                            }}
                                        >
                                            <LabelList
                                                dataKey={s.key}
                                                position="top"
                                                dy={
                                                    s.key === "stockAmount"
                                                        ? -10
                                                        : -4
                                                }
                                                formatter={valueFormatter}
                                                style={{
                                                    fontSize: 8,
                                                    fill: "#11111180",
                                                    fontWeight:400
                                                }}
                                            />
                                        </Line>
                                    );
                                }

                                return null;
                            })}
                        </ComposedChart>
                    </ResponsiveContainer>
                </Box>

            </Box>


            {/* LEGEND */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    m: '16px'
                }}
            >
                {series.map((s) => (
                    <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: s.color
                            }}
                        />
                        <Typography fontSize={10} style={{ color: "#111111",fontWeight:200 }}>{s.label}</Typography>
                    </Box>
                ))}
            </Box>
        </Card>
    );
}
